'use client';

import React, { useState, createContext, useContext, useEffect } from 'react';
import { SurveyModal } from './SurveyModal';
import { useSurveyStore } from '@/store/store';
import { DEFAULT_SURVEY_ID, DEFAULT_SURVEY_TITLE } from './survey-constants';

interface SurveyModalsContextType {
  openSurveyModal: (surveyId: string, title?: string) => void;
  closeSurveyModal: () => void;
}

const SurveyModalsContext = createContext<SurveyModalsContextType | null>(null);

export function useSurveyModal() {
  const context = useContext(SurveyModalsContext);
  if (!context) {
    throw new Error('useSurveyModal must be used within SurveyModalsProvider');
  }
  return context;
}

interface SurveyModalsManagerProps {
  children?: React.ReactNode;
}

export function SurveyModalsManager({ children }: SurveyModalsManagerProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [surveyId, setSurveyId] = useState('');
  const [title, setTitle] = useState(DEFAULT_SURVEY_TITLE);
  const { showSurvey, setShowSurvey } = useSurveyStore();

  const openSurveyModal = (id: string, surveyTitle?: string) => {
    if (!id?.trim()) return;
    setSurveyId(id.trim());
    setTitle(surveyTitle?.trim() || DEFAULT_SURVEY_TITLE);
    setShowSurvey(false); // avoid double-open from store
    setIsOpen(true);
  };

  const closeSurveyModal = () => {
    setIsOpen(false);
    setShowSurvey(false);
  };

  // When store showSurvey is true (e.g. after last lesson complete), open modal with default survey ID (academy-1.0 behavior).
  // Clear the persisted flag the moment it's consumed, not just when the modal is later closed — otherwise a full-page
  // navigation between "last lesson complete" and closing the modal (e.g. a login reload) leaves showSurvey stuck true
  // in localStorage, and it re-opens the survey on every future page load, including unrelated ones.
  useEffect(() => {
    if (showSurvey && !isOpen) {
      setSurveyId(DEFAULT_SURVEY_ID);
      setTitle(DEFAULT_SURVEY_TITLE);
      setIsOpen(true);
      setShowSurvey(false);
    }
  }, [showSurvey, isOpen, setShowSurvey]);

  const contextValue: SurveyModalsContextType = {
    openSurveyModal,
    closeSurveyModal,
  };

  return (
    <SurveyModalsContext.Provider value={contextValue}>
      {children}
      {isOpen && surveyId && (
        <SurveyModal
          surveyId={surveyId}
          title={title}
          open={isOpen}
          onOpenChange={(open) => {
            if (!open) closeSurveyModal();
          }}
          onClose={closeSurveyModal}
        />
      )}
    </SurveyModalsContext.Provider>
  );
}
